/**
 * API Configuration for Microservice Architecture
 * Routes different types of requests to appropriate microservices
 */

// Microservice endpoints
const MICROSERVICES = {
  // Main API Gateway - handles auth, news, and proxying
  MAIN_API: process.env.REACT_APP_API_URL || "https://api.notmyview.com",

  // Individual microservices (can be used for direct calls if needed)
  BIAS_ANALYSIS: process.env.REACT_APP_BIAS_URL || "https://bias.notmyview.com",
  RSS_AGGREGATION: process.env.REACT_APP_RSS_URL || "https://rss.notmyview.com",
  STORY_PROCESSING:
    process.env.REACT_APP_STORIES_URL || "https://stories.notmyview.com",
  SCHEDULED_TASKS:
    process.env.REACT_APP_TASKS_URL || "https://tasks.notmyview.com",
  API_GATEWAY:
    process.env.REACT_APP_GATEWAY_URL || "https://gateway.notmyview.com",
  MONITORING:
    process.env.REACT_APP_MONITOR_URL || "https://monitor.notmyview.com",
};

// Route mapping - determines which service handles which endpoints
const ROUTE_MAP = {
  // Authentication routes - handled by main API
  "/authentication/request": "MAIN_API",
  "/auth/verify": "MAIN_API",
  "/auth/validate": "MAIN_API",
  "/session": "MAIN_API",

  // News and stories - handled by main API (which proxies to story service)
  "/news/stories": "MAIN_API",
  "/news/story": "MAIN_API",

  // Admin endpoints - routed through main API gateway
  "/admin/api-keys": "MAIN_API",
  "/admin/logs": "MAIN_API",
  "/admin/rss": "MAIN_API",
  "/admin/rss-config": "MAIN_API",
  "/admin/story-clustering": "MAIN_API",
  "/admin/jobs": "MAIN_API",
  "/admin/webhooks": "MAIN_API",
  "/admin/cleanup": "MAIN_API",
  "/admin/bias": "MAIN_API",

  // Bias checking - can go direct to bias service or through main API
  "/bias-check": "MAIN_API", // Using main API for consistency

  // Health checks
  "/health": "MAIN_API",

  // Usage tracking
  "/usage": "MAIN_API",
};

/**
 * Get the appropriate API endpoint for a given path
 * @param {string} path - The API path (e.g., '/auth/validate')
 * @returns {string} - The full URL for the request
 */
export function getApiEndpoint(path) {
  // Remove leading slash for consistent matching
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  // Check for exact matches first
  if (ROUTE_MAP[normalizedPath]) {
    return MICROSERVICES[ROUTE_MAP[normalizedPath]];
  }

  // Check for prefix matches
  for (const [routePrefix, service] of Object.entries(ROUTE_MAP)) {
    if (normalizedPath.startsWith(routePrefix)) {
      return MICROSERVICES[service];
    }
  }

  // Default to main API for unknown routes
  return MICROSERVICES.MAIN_API;
}

/**
 * Build a complete API URL
 * @param {string} path - The API path
 * @param {Object} params - Query parameters (optional)
 * @returns {string} - Complete URL
 */
export function buildApiUrl(path, params = {}) {
  const baseUrl = getApiEndpoint(path);
  const url = new URL(path, baseUrl);

  // Add query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      url.searchParams.append(key, value);
    }
  });

  return url.toString();
}

/**
 * Enhanced fetch wrapper that automatically routes to correct microservice
 * @param {string} path - API path
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export async function apiFetch(path, options = {}) {
  const url = buildApiUrl(path);

  // Add default headers
  const defaultHeaders = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  // Add authentication if available
  const sessionToken = localStorage.getItem("sessionToken");
  const apiKey = localStorage.getItem("balanceApiKey");

  if (sessionToken) {
    defaultHeaders.Authorization = `Bearer ${sessionToken}`;
  } else if (apiKey) {
    defaultHeaders.Authorization = `Bearer ${apiKey}`;
  }

  const fetchOptions = {
    ...options,
    headers: defaultHeaders,
  };

  console.log(`🌐 API Request: ${options.method || "GET"} ${url}`);

  try {
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      console.error(
        `❌ API Error: ${response.status} ${response.statusText} for ${url}`
      );
    } else {
      console.log(`✅ API Success: ${response.status} for ${url}`);
    }

    return response;
  } catch (error) {
    console.error(`🚨 Network Error for ${url}:`, error);
    throw error;
  }
}

/**
 * Get service health status
 * @returns {Promise<Object>} - Health status of all services
 */
export async function getServiceHealth() {
  const healthChecks = Object.entries(MICROSERVICES).map(
    async ([name, url]) => {
      try {
        const response = await fetch(`${url}/health`, {
          method: "GET",
          headers: { "User-Agent": "Frontend-Health-Check" },
        });

        const data = await response.json();
        return {
          name,
          url,
          status: response.ok ? "healthy" : "unhealthy",
          responseTime: data.timestamp
            ? new Date() - new Date(data.timestamp)
            : null,
          lastCheck: new Date().toISOString(),
        };
      } catch (error) {
        return {
          name,
          url,
          status: "error",
          error: error.message,
          lastCheck: new Date().toISOString(),
        };
      }
    }
  );

  return await Promise.all(healthChecks);
}

// Export configuration for debugging
export const API_CONFIG = {
  MICROSERVICES,
  ROUTE_MAP,
};

// Legacy compatibility - export the main API URL
export const API_BASE = MICROSERVICES.MAIN_API;
export const BACKEND_URL = `${MICROSERVICES.MAIN_API}/bias-check`;
export const AUTH_REQUEST_URL = `${MICROSERVICES.MAIN_API}/authentication/request`;
export const AUTH_VERIFY_URL = `${MICROSERVICES.MAIN_API}/auth/verify`;
export const AUTH_VALIDATE_URL = `${MICROSERVICES.MAIN_API}/auth/validate`;
export const USAGE_URL = `${MICROSERVICES.MAIN_API}/usage`;
